import React from "react";
import { Button } from "@design-system/Button/Button";
import { allSizes, linkSizes, sizesFrom24 } from "./ButtonPlayground.lib";
import styles from "./ButtonPlayground.module.scss";

/**
 * The `data-forced-state` contract is playground-only: `Button.module.scss`
 * mirrors every `:hover` / `:active` / `:focus-visible` rule onto it so the
 * sheet can show all five states at once, the way Figma lays them out.
 */
type ForcedState = "hover" | "pressed" | "focus";

interface MatrixStateProps {
    disabled?: boolean;
    "data-forced-state"?: ForcedState;
}

const matrixStates: ReadonlyArray<{
    id: string;
    label: string;
    props: MatrixStateProps;
}> = [
    { id: "normal", label: "Normal", props: {} },
    { id: "hover", label: "Hover", props: { "data-forced-state": "hover" } },
    {
        id: "pressed",
        label: "Pressed",
        props: { "data-forced-state": "pressed" },
    },
    { id: "focus", label: "Focus", props: { "data-forced-state": "focus" } },
    { id: "disabled", label: "Disabled", props: { disabled: true } },
];

interface MatrixRow {
    id: string;
    label: string;
    renderButton: (stateProps: MatrixStateProps) => React.ReactNode;
}

interface MatrixGroup {
    id: string;
    title: string;
    rows: readonly MatrixRow[];
}

// Each group spells its variant out literally so the discriminated union
// enforces the legal sizes — no casts, and an illegal combo cannot compile.
const matrixGroups: readonly MatrixGroup[] = [
    {
        id: "primary",
        title: "Primary",
        rows: allSizes.map((size) => ({
            id: `primary-${size}`,
            label: size,
            renderButton: (stateProps: MatrixStateProps) => (
                <Button variant="primary" size={size} {...stateProps}>
                    Button
                </Button>
            ),
        })),
    },
    {
        id: "secondary",
        title: "Secondary",
        rows: allSizes.map((size) => ({
            id: `secondary-${size}`,
            label: size,
            renderButton: (stateProps: MatrixStateProps) => (
                <Button variant="secondary" size={size} {...stateProps}>
                    Button
                </Button>
            ),
        })),
    },
    {
        id: "tertiary",
        title: "Tertiary",
        rows: sizesFrom24.map((size) => ({
            id: `tertiary-${size}`,
            label: size,
            renderButton: (stateProps: MatrixStateProps) => (
                <Button variant="tertiary" size={size} {...stateProps}>
                    Button
                </Button>
            ),
        })),
    },
    {
        id: "destructive",
        title: "Destructive",
        rows: sizesFrom24.map((size) => ({
            id: `destructive-${size}`,
            label: size,
            renderButton: (stateProps: MatrixStateProps) => (
                <Button variant="destructive" size={size} {...stateProps}>
                    Button
                </Button>
            ),
        })),
    },
    {
        id: "ghost",
        title: "Ghost",
        rows: [
            {
                id: "ghost-s-regular",
                label: "md · s-regular",
                renderButton: (stateProps: MatrixStateProps) => (
                    <Button
                        variant="ghost"
                        size="md"
                        labelStyle="s-regular"
                        {...stateProps}
                    >
                        Button
                    </Button>
                ),
            },
            {
                id: "ghost-xs-medium",
                label: "md · xs-medium",
                renderButton: (stateProps: MatrixStateProps) => (
                    <Button
                        variant="ghost"
                        size="md"
                        labelStyle="xs-medium"
                        {...stateProps}
                    >
                        Button
                    </Button>
                ),
            },
        ],
    },
    {
        id: "accent",
        title: "Accent",
        rows: [
            {
                id: "accent-md",
                label: "md",
                renderButton: (stateProps: MatrixStateProps) => (
                    <Button variant="accent" size="md" {...stateProps}>
                        Button
                    </Button>
                ),
            },
        ],
    },
    {
        id: "link",
        title: "Link",
        rows: linkSizes.map((size) => ({
            id: `link-${size}`,
            label: size,
            renderButton: (stateProps: MatrixStateProps) => (
                <Button variant="link" size={size} {...stateProps}>
                    Button
                </Button>
            ),
        })),
    },
];

/**
 * @purpose The Figma state sheet, rendered live: every variant's legal sizes
 * against the five states. Hover, pressed and focus are forced via
 * `data-forced-state`; tab to a button to compare against the real
 * `:focus-visible` ring.
 */
export const ButtonMatrix = () => (
    <section className={styles.matrix}>
        <h2 className={styles.matrixTitle}>Variant × size × state</h2>
        {matrixGroups.map((group) => (
            <div key={group.id} className={styles.group}>
                <h3 className={styles.groupTitle}>{group.title}</h3>
                <div className={styles.grid}>
                    <span />
                    {matrixStates.map((state) => (
                        <span key={state.id} className={styles.columnHeader}>
                            {state.label}
                        </span>
                    ))}
                    {group.rows.map((row) => (
                        <React.Fragment key={row.id}>
                            <span className={styles.rowLabel}>{row.label}</span>
                            {matrixStates.map((state) => (
                                <div key={state.id} className={styles.cell}>
                                    {row.renderButton(state.props)}
                                </div>
                            ))}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        ))}
    </section>
);
